import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, Lock, Star, MessageSquare } from 'lucide-react'
import { PublicLayout } from '@/templates/PublicLayout'
import { getFoundingSlots, type FoundingSlots } from '@/api/founding'

export default function LandingPage() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background: 'linear-gradient(180deg, var(--cream) 0%, var(--bone) 100%)',
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 20%, rgba(245, 144, 74, 0.06) 0%, transparent 40%),
              radial-gradient(circle at 80% 80%, rgba(245, 144, 74, 0.04) 0%, transparent 40%)
            `,
          }}
        />

        <div className="relative z-10 px-5 pt-20 pb-16 md:pt-28 md:pb-24 text-center max-w-3xl mx-auto">
          <div className="brand-mark brand-mark--lg mx-auto mb-8">p</div>

          <h1 className="font-serif text-[40px] sm:text-5xl md:text-7xl font-semibold tracking-[-0.03em] leading-[1.05] mb-5">
            Plan your dream event in the{' '}
            <em className="font-serif italic text-orange-deep font-semibold">Maldives</em>
          </h1>

          <p className="text-base md:text-lg text-ink-3 max-w-xl mx-auto mb-8 leading-relaxed">
            Discover and book photographers, venues, caterers, decorators, makeup artists, bakers,
            and models — all in one place.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center max-w-md mx-auto">
            <Link
              to="/discover"
              className="px-6 py-3 bg-ink text-cream rounded-pill font-semibold hover:bg-orange transition-colors"
            >
              Browse vendors
            </Link>
            <Link
              to="#founding"
              className="px-6 py-3 bg-white text-ink border border-line rounded-pill font-semibold hover:border-ink-3 transition-colors"
            >
              For vendors →
            </Link>
          </div>

          <div className="mt-12 inline-flex items-center gap-3 text-xs uppercase tracking-[0.1em] font-medium text-ink/50">
            <span className="w-8 h-px bg-ink/15" />
            In development · v0.1
            <span className="w-8 h-px bg-ink/15" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-5 py-16 md:py-20 max-w-5xl mx-auto">
        <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-[-0.02em] text-center mb-12">
          Everything you need, in one place
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <FeatureCard
            emoji="📷"
            title="7 vendor types"
            body="Photography, venues, catering, decoration, MUA, bakers, models. Every category for your event."
          />
          <FeatureCard
            emoji="💬"
            title="Direct messaging"
            body="Chat with vendors, share inspiration, get quotes — all without leaving the platform."
          />
          <FeatureCard
            emoji="📊"
            title="Plan your budget"
            body="Track expenses, share with your partner, and stay on budget with the Plan dashboard."
          />
        </div>
      </section>

      {/* Founding member program */}
      <FoundingSection />

      {/* Status banner */}
      <section className="px-5 pb-16 max-w-3xl mx-auto">
        <div className="bg-orange-soft border border-orange-200 rounded-2xl p-6 text-center">
          <h3 className="font-serif text-lg font-semibold mb-1">Coming soon</h3>
          <p className="text-sm text-ink-2 leading-relaxed">
            We're currently building PlanSync. Beta launch with select photographers in 2026.
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}

function FoundingSection() {
  const [photoSlots, setPhotoSlots] = useState<FoundingSlots | null>(null)

  useEffect(() => {
    let mounted = true
    getFoundingSlots('photography')
      .then(s => mounted && setPhotoSlots(s))
      .catch(() => mounted && setPhotoSlots(null))
    return () => {
      mounted = false
    }
  }, [])

  return (
    <section
      id="founding"
      className="relative overflow-hidden scroll-mt-20"
      style={{
        background: 'linear-gradient(135deg, #1C1814 0%, #2a241f 100%)',
      }}
    >
      {/* Glow accent */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(circle at 80% 0%, rgba(245, 144, 74, 0.18), transparent 50%)',
        }}
      />

      <div className="relative z-10 px-5 py-16 md:py-24 max-w-5xl mx-auto text-cream">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-orange mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Founding member program
          </div>
          <h2 className="font-serif text-3xl md:text-5xl font-semibold tracking-[-0.02em] leading-tight mb-4">
            For the first <em className="italic text-orange">5 photographers</em>
          </h2>
          <p className="text-base md:text-lg text-cream/80 leading-relaxed">
            We're recruiting our founding cohort. Be among the first
            photographers on PlanSync, lock in founding pricing for
            24 months, and shape the platform alongside us.
          </p>
        </div>

        {/* Slot counter */}
        <div className="max-w-md mx-auto mb-10">
          <SlotCounter slots={photoSlots} />
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-10">
          <BenefitCard
            icon={Lock}
            title="Locked pricing for 24 months"
            body="Pro Rf 299 / month or Studio Rf 999 / month. Standard rates after launch are 60–67% higher (Rf 499 / Rf 1,499)."
          />
          <BenefitCard
            icon={Star}
            title="Founding-member badge"
            body="A visible mark on your public profile and Discover card so customers know you were first to back PlanSync."
          />
          <BenefitCard
            icon={Sparkles}
            title="First pick of inquiries"
            body="With only 5 photographers live, you'll see every customer that comes through. No fighting for visibility."
          />
          <BenefitCard
            icon={MessageSquare}
            title="Direct line to the team"
            body="Telegram and email access to the founder. Your feedback shapes what we build next."
          />
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/auth/vendor-application"
            className={`inline-flex items-center gap-2 px-8 py-4 rounded-pill font-semibold text-base transition-colors ${
              photoSlots?.full
                ? 'bg-cream/10 text-cream/40 cursor-not-allowed pointer-events-none'
                : 'bg-orange text-white hover:bg-orange-deep'
            }`}
          >
            {photoSlots?.full ? 'Founding cohort full' : 'Apply now'}
            {!photoSlots?.full && <Sparkles className="w-4 h-4" />}
          </Link>
          <p className="mt-4 text-xs text-cream/50">
            Rf 200 application fee · non-refundable · we review every applicant
          </p>
        </div>
      </div>
    </section>
  )
}

function SlotCounter({ slots }: { slots: FoundingSlots | null }) {
  const filled = slots?.filled ?? 0
  const total = slots?.total ?? 5
  const remaining = slots?.remaining ?? 5
  const pct = (filled / total) * 100

  return (
    <div className="rounded-2xl bg-cream/5 border border-cream/10 backdrop-blur-sm p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.12em] font-semibold text-cream/60">
          Photography slots
        </div>
        <div className="font-serif text-2xl font-semibold">
          {slots === null ? '…' : (
            <>
              <span className="text-orange">{remaining}</span>
              <span className="text-cream/50 text-base font-normal"> of {total} left</span>
            </>
          )}
        </div>
      </div>
      <div className="h-2 bg-cream/10 rounded-pill overflow-hidden flex gap-0.5">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`flex-1 transition-colors ${
              i < filled ? 'bg-orange-deep' : 'bg-cream/15'
            }`}
          />
        ))}
      </div>
      <div className="mt-2.5 text-[11px] text-cream/50 flex justify-between">
        <span>{filled} approved</span>
        <span className="font-mono">{Math.round(pct)}% full</span>
      </div>
    </div>
  )
}

function BenefitCard({
  icon: Icon,
  title,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  body: string
}) {
  return (
    <div className="rounded-2xl bg-cream/5 border border-cream/10 backdrop-blur-sm p-5">
      <div className="w-10 h-10 rounded-xl bg-orange/15 text-orange inline-flex items-center justify-center mb-3">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-serif text-lg font-semibold tracking-tight mb-1.5">{title}</h3>
      <p className="text-sm text-cream/70 leading-relaxed">{body}</p>
    </div>
  )
}

function FeatureCard({
  emoji,
  title,
  body,
}: {
  emoji: string
  title: string
  body: string
}) {
  return (
    <div className="rounded-2xl bg-cream border border-ink/[0.04] p-6 transition-all duration-200 hover:border-ink/[0.08] hover:-translate-y-px hover:shadow-[0_6px_20px_-8px_rgba(28,24,20,0.08)]">
      <div className="text-3xl mb-3">{emoji}</div>
      <h3 className="font-semibold mb-2">{title}</h3>
      <p className="text-sm text-ink-3 leading-relaxed">{body}</p>
    </div>
  )
}
